<template>
  <div class="performance-chart">
    <HelperViewStatus
      v-if="hasError"
      class="performance-chart__helper"
      title="Não foi possível carregar os dados de performance da sua carteira"
      description="Tente buscar novamente"
      button-label="Tentar novamente"
      status="alert"
      @click="emitRetry"
    />
    <template v-else>
      <LineChart
        data-testid="performance-chart"
        :series="portfolioData"
        :benchmark="benchmarkData"
        :labels="labels"
        :show-axis-x-labels="false"
        :visible-tooltips-index="[0, 1]"
        :is-loading="isLoading"
        :height="height"
        no-data-message="Não há dados de investimentos. Ao investir, atualizará em até 2 dias úteis."
        @on-animation-state-change="handleOnAnimationStateChange"
      />
      <ChartFooterV2
        :left-text="range"
        :right-text="props.updatedAt"
        :is-loading="isLoading"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import {
  LineChart,
  ChartFooterV2
} from '@/modules/common/components/charts/v2';
import { HelperViewStatus } from '@warrenbrasil/nebraska-web';

import {
  PortfolioChartAxisX,
  PortfolioChartAxisY,
  PortfolioChartAxisYValues,
  PortfolioChartFilter
} from '@/modules/wealth/services/portfolio';

interface Props {
  axisX: PortfolioChartAxisX[];
  axisY: PortfolioChartAxisY[];
  benchmarkFilters: PortfolioChartFilter[];
  updatedAt?: string;
  isLoading?: boolean;
  hasError?: boolean;
  height?: string;
}
const props = withDefaults(defineProps<Props>(), {
  updatedAt: '',
  height: '400px'
});

interface Emits {
  (e: 'on-animation-state-change', value: boolean): void;
  (e: 'on-retry'): void;
}
const emit = defineEmits<Emits>();

const portfolioData = computed(() => {
  const portfolio = props.axisY?.find(
    axis => axis.type === PortfolioChartAxisYValues.Portfolio
  );
  const profit = props.axisY?.find(
    axis => axis.type === PortfolioChartAxisYValues.Profit
  );

  return {
    name: portfolio?.name || '-',
    data: portfolio?.values.map(value => (value ? value.value : null)) || [],
    comparator:
      profit?.values.map(value => (value ? value.formatted : null)) || [],
    benchmarkComparison:
      portfolio?.values.map(value =>
        value ? value.benchmarkComparison : null
      ) || []
  };
});

const benchmarkData = computed(() => {
  const benchmark = props.axisY?.find(
    axis => axis.name === activeBenchmarkFilter.value?.label
  );

  return {
    name: activeBenchmarkFilter.value?.summaryLabel || '-',
    data: benchmark?.values.map(value => (value ? value.value : null)) || []
  };
});

const range = computed(() => {
  const startDate = props.axisX?.[0]?.range?.start?.formatted;
  const endDate = props.axisX?.[0]?.range?.end?.formatted;

  return startDate && endDate ? `${startDate} a ${endDate}` : '-';
});

const labels = computed(() => {
  return props.axisX?.[0]?.values?.map(value => value.value) || [];
});

const activeBenchmarkFilter = computed(() => {
  return props.benchmarkFilters.find(filter => filter.active);
});

const handleOnAnimationStateChange = (value: boolean) => {
  emit('on-animation-state-change', value);
};

const emitRetry = () => {
  emit('on-retry');
};
</script>

<style lang="less" scoped>
.performance-chart {
  margin-top: @size-spacing-x450;

  &__helper {
    margin: @size-spacing-x600 auto @size-spacing-x600 auto;
  }
}
</style>
